import { Link } from "react-router-dom";
import { Check, ChevronRight, Phone, Zap, Sparkles, Flower2, Eye, Droplets, ArrowRight } from "lucide-react";

export function FullGlowPackage({ ctaTo }) {
    const items = [
        "Full Set 3D AI Nail Art & Gel Polish",
        "Norvell® Sunless Spray Tan",
        "Hydrating Express Facial with LED Light Mask",
        "Lash Lift & Tint or Classic Set",
    ];
    return (
        <section id="full-glow-package" data-testid="full-glow-section" className="relative py-24 border-y border-hairline bg-surface">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-12 gap-10 items-center">
                <div className="reveal lg:col-span-6">
                    <p className="text-xs tracking-[0.3em] uppercase text-gold">The Full Glow Package</p>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mt-3">
                        The ultimate <span className="italic text-rose">head-to-toe</span> VIP experience.
                    </h2>
                    <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-xl">
                        One appointment. Every treatment. Nails, tan, facial and lashes — delivered to your door in a single luxury session.
                    </p>
                    <ul className="mt-8 space-y-3">
                        {items.map((f) => (
                            <li key={f} className="flex gap-3 text-white/85">
                                <Check className="w-5 h-5 text-gold mt-0.5 shrink-0" /> {f}
                            </li>
                        ))}
                    </ul>
                    <Link
                        to={ctaTo}
                        data-testid="full-glow-cta"
                        className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-ink font-semibold hover:opacity-90 transition shadow-lg shadow-gold/20"
                    >
                        Book the Full Glow <ChevronRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="reveal lg:col-span-6">
                    <div className="aspect-[3/2] rounded-3xl overflow-hidden border border-hairline shadow-xl shadow-black/40">
                        <img src="/gallery/full-glow-package.jpg" alt="Full Glow Package — nails, LED mask, lashes" className="w-full h-full object-cover" />
                    </div>
                </div>
            </div>
        </section>
    );
}

export function EmergencyGlam() {
    return (
        <section id="emergency-glam" data-testid="emergency-glam-section" className="relative py-24 bg-surface border-y border-hairline overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-rose/10 via-transparent to-gold/10 pointer-events-none" />
            <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 text-center">
                <p className="reveal text-xs tracking-[0.4em] uppercase text-rose flex items-center justify-center gap-2">
                    <Zap className="w-4 h-4" /> Emergency Glam · Same-Day Express
                </p>
                <h2 className="reveal font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">
                    Need last-minute glam <span className="italic text-rose">for tonight?</span>
                </h2>
                <p className="reveal mt-6 text-white/70 text-lg max-w-2xl mx-auto">
                    Same-day mobile dispatch in 30 min – 1 hour. Your service rate + a $75 emergency dispatch upcharge — and we're at your door, kit in hand.
                </p>
                <div className="reveal mt-10 flex flex-col items-center gap-4">
                    <a
                        href="tel:9046729966"
                        data-testid="emergency-glam-call-button"
                        className="inline-flex items-center justify-center gap-3 px-10 py-6 rounded-full bg-rose text-ink font-bold text-xl sm:text-2xl hover:bg-roseHover transition shadow-2xl shadow-rose/30 animate-pulse-rose w-full sm:w-auto"
                    >
                        <Phone className="w-7 h-7" /> Call Now: (904) 672-9966
                    </a>
                    <p className="text-xs text-white/50 tracking-widest uppercase">Tap to call · we answer fast</p>
                </div>
            </div>
        </section>
    );
}

export function WaxUvLashes({ ctaTo }) {
    const cards = [
        {
            icon: Flower2,
            img: null,
            name: "Waxing Services",
            desc: "Precision brow shaping, lip, chin, and silky full-face wax treatments — gentle, salon-grade wax at your door.",
        },
        {
            icon: Sparkles,
            img: "/gallery/led-mask.jpg",
            name: "UV LED Light Mask",
            desc: "7 targeted wavelengths: Red 630nm (collagen & anti-aging), Blue 470nm (acne & bacteria), Green, Yellow, Turquoise, Purple & White.",
        },
        {
            icon: Eye,
            img: "/gallery/lashes-real.jpg",
            real: true,
            name: "Lashes",
            desc: "Classic sets, lifts & tints tailored to your eye shape — wake up camera-ready every day. Pictured: one eye done, one bare — see the difference.",
        },
    ];
    return (
        <section id="wax-uv-lashes" data-testid="wax-uv-lashes-section" className="relative py-28">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
                <div className="reveal max-w-3xl">
                    <p className="text-xs tracking-[0.3em] uppercase text-gold">Beyond nails</p>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">
                        Waxing, UV light <span className="italic text-rose">& lashes.</span>
                    </h2>
                </div>
                <div className="reveal mt-12 grid md:grid-cols-3 gap-6">
                    {cards.map(({ icon: Icon, img, name, desc, real }) => (
                        <div key={name} data-testid={`service-card-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-3xl border border-hairline bg-surface overflow-hidden hover:border-gold/50 transition group">
                            {img ? (
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    <img src={img} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                                    {real && (
                                        <span data-testid="lashes-real-badge" className="absolute top-3 left-3 px-3 py-1 rounded-full bg-gold text-ink text-[11px] font-semibold tracking-wide uppercase shadow">
                                            Real client result
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <div className="aspect-[4/3] bg-surface2 flex items-center justify-center">
                                    <Icon className="w-16 h-16 text-rose/60" />
                                </div>
                            )}
                            <div className="p-7">
                                <div className="flex items-center gap-3">
                                    <Icon className="w-5 h-5 text-gold" />
                                    <h3 className="font-serif text-2xl">{name}</h3>
                                </div>
                                <p className="mt-3 text-sm text-white/65 leading-relaxed">{desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="reveal mt-10">
                    <Link to={ctaTo} data-testid="wax-uv-lashes-cta" className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-gold/50 text-gold hover:bg-gold hover:text-ink transition font-medium">
                        Book any of these <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </section>
    );
}

export function NorvellTans({ ctaTo }) {
    const products = [
        { name: "Norvell® Venetian", benefit: "Cool violet undertones cancel orange for an exotic Mediterranean bronze." },
        { name: "Norvell® Black Out", benefit: "Deepest bronze finish — built for stage lights, galas and competitions." },
        { name: "Norvell® Rapid ONE", benefit: "Shower in as little as 1–3 hours with custom development intensity." },
    ];
    return (
        <section id="norvell-tans" data-testid="norvell-section" className="relative py-28 bg-surface border-y border-hairline">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-12 gap-12 items-center">
                <div className="reveal lg:col-span-5 grid grid-cols-2 gap-3">
                    <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-hairline col-span-1 row-span-2">
                        <img src="/gallery/spray-tan-glow.jpg" alt="Flawless sunless spray tan glow" className="w-full h-full object-cover" />
                    </div>
                    <div className="aspect-square rounded-2xl overflow-hidden border border-hairline col-span-1 row-span-2">
                        <img src="/gallery/norvell-products.jpg" alt="Professional sunless tanning products" className="w-full h-full object-cover" />
                    </div>
                </div>
                <div className="reveal lg:col-span-7">
                    <p className="text-xs tracking-[0.3em] uppercase text-gold">Norvell® Sunless</p>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">
                        Vegan, streak-proof <span className="italic text-rose">Norvell spray tans.</span>
                    </h2>
                    <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-2xl">
                        We exclusively use professional Norvell® solutions — the gold standard of sunless. Custom-matched to your skin tone, airbrushed at your door.
                    </p>
                    <div className="mt-8 space-y-4">
                        {products.map((p) => (
                            <div key={p.name} data-testid={`norvell-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="p-5 rounded-2xl border border-hairline bg-ink/40 hover:border-gold/50 transition">
                                <div className="flex items-center gap-3">
                                    <Droplets className="w-5 h-5 text-rose shrink-0" />
                                    <h3 className="font-serif text-xl text-gold">{p.name}</h3>
                                </div>
                                <p className="mt-2 text-sm text-white/65 ml-8">{p.benefit}</p>
                            </div>
                        ))}
                    </div>
                    <Link to={ctaTo} data-testid="norvell-cta" className="mt-9 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-rose text-ink font-semibold hover:bg-roseHover transition">
                        Book a Spray Tan <ChevronRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </section>
    );
}

export function FacialsTeaser() {
    return (
        <section id="facials-teaser" data-testid="facials-teaser-section" className="relative py-28">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-12 gap-10 items-center">
                <div className="reveal lg:col-span-7">
                    <p className="text-xs tracking-[0.3em] uppercase text-gold">Custom facials</p>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">
                        Skin that <span className="italic text-rose">behaves.</span>
                    </h2>
                    <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-2xl">
                        Personalized vegan facials with LED light therapy, matched to your skin's mood. Not sure where to start?
                        Take the 60-second Skin Quiz and we'll match you to your perfect treatment.
                    </p>
                    <div className="mt-9 flex flex-wrap gap-4">
                        <Link to="/skin-quiz" data-testid="home-skin-quiz-cta" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-ink font-semibold hover:opacity-90 transition shadow-lg shadow-gold/20">
                            <Sparkles className="w-4 h-4" /> Take the Skin Quiz
                        </Link>
                        <Link to="/facials" data-testid="home-facials-cta" className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-rose/50 text-white hover:bg-rose hover:text-ink transition">
                            Explore Facials <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
                <div className="reveal lg:col-span-5">
                    <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-hairline">
                        <img src="/gallery/led-mask.jpg" alt="LED light mask facial" className="w-full h-full object-cover" />
                    </div>
                </div>
            </div>
        </section>
    );
}
